import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  HostListener,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  NgbDateParserFormatter,
  NgbDate,
  NgbDateStruct,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { DatePipe } from '@angular/common';
import { FnDateFormatter } from '../../util/date';
import { FnToasterService } from '../toaster/index';
import { FNDateRangeModel } from '../date-picker/fn-date.model';
import { isNull } from '../../util/check';

/**
 * This Component create DateRangePicker with 2 months And allows users to select start and end Date.
 * To use this component import FnDateRangePickerModule in Module.
 */
@Component({
  selector: 'fn-date-range-picker',
  templateUrl: './fn-date-range-picker.component.html',
  providers: [FnDateFormatter],
  encapsulation: ViewEncapsulation.None,
})
export class FNDateRangePickerComponent implements OnInit {

  @ViewChild(NgbInputDatepicker) ngbDatePicker: NgbInputDatepicker;
  /**
   * Input Member Variable of Type Date
   * used to store start Date
   */
  @Input() startDate: Date;
  /**
   * Input Member Variable of Type Date
   * used to store end Date
   */
  @Input() endDate: Date;
  /**
   * Input Member Variable ,default Value is false.
   * if true then disable the date greater then current Date and set Max date to current Date.
   */
  @Input() futureDateDisabled = false;
  /**
   * Input Member Variable of Type Date ,default Value is null.
   * used set Max Date in Calender and also compare with date to disable date greater than max date if futureDateDisabled is true.
   */
  @Input() maxDate: Date = null;
  /**
   * Input Member Variable of Type Date ,default Value is null.
   * used to show the minimum date in calender that user can select.
   */
  @Input() minDate: Date = null;
  /**
   * Input Member Variable
   * used to show the date in given Format.
   * default value is 'Y/M/d' date show in Format yyyy/mm/dd.
   */
  @Input() dateFormat = 'Y/m/d';
  /**
   * Output  Member Variable
   * emit event When user Click On Apply Button after selecting Start and End Date.
   */
  @Output() dateCB = new EventEmitter<FNDateRangeModel>();
  @Input() enableDateRange = false;
  @Input() maxDayNum = 56;
  @Input() validationMsg = '';
  @Input() container = '';
  /**
   * Member Variable of Type NgbDateStruct
   * used to store start Date in the Format datepicker use. And the date to open calendar with.
   */
  fnStartDate: NgbDateStruct;
  /**
   * Member Variable of Type NgbDateStruct
   * used to store End Date in the Format datepicker use
   */
  fnEndDate: NgbDateStruct;
  /**
   * Member Variable of Type NgbDateStruct
   * used To store Date on which mouse is Hover in DatePicker.
   */
  hoveredDate: NgbDateStruct;
  /**
   * Member Variable of Type NgbDateStruct
   * used to Bind Max Date with DatePicker.
   */
  fnMaxDate: NgbDateStruct;
  /**
   * Member Variable of Type NgbDateStruct
   * used to Bind Min Date with DatePicker.
   */
  fnMinDate: NgbDateStruct;
  /**
   * Member Variable of Type NgbDateStruct
   * used to store start and end date seprate with '-' and show on UI.
   */
  uiDateValue: string;
  /**
   * Member Variable
   * used to open and Close Date picker.
   */
  isOpened = false;
  /**
   * Member Variable
   * The reference to a custom template for the day.
   * Allows to completely override the way a day 'cell' in the calendar is displayed
   */
  dayTemplate: any;
  validButton = false;
  private isAddPrevClass = false;
  /**
   * Member Method
   * Method Call when user click on DatePicker Field.
   * Open when isOpen is True and close when isOpen is False.
   * @param datePicker NgbInputDatepicker object used to call Open And Close Method of DatePicker.
   */
  openDatePicker(datePicker: NgbInputDatepicker) {
    this.isOpened = !this.isOpened;
    this.isOpened ? datePicker.open() : this.resetDate(datePicker);
  }
  /**
   * Creates an instance of fndate range picker component.
   * @param formatter Object to Parses the given DateObejct string to an NgbDateStruct. And Format NgbDateStruct to Date String
   * @param datePipe Object User to transform Date Object to Date String TO format date.
   * @param fnDateFormattter Object used to Format date To show ON UI.
   */
  constructor(
    public formatter: NgbDateParserFormatter,
    private datePipe: DatePipe,
    private fnDateFormattter: FnDateFormatter,
    private fnToaster: FnToasterService,
    private elemRef: ElementRef
  ) { }

  /**
   * Implementing ngOnInit Event Handler
   * setting Defaut Values of member Variables.
   */
  ngOnInit() {
    this.setDefaultDate();
    if (this.minDate !== null) {
      this.fnMaxDate = this.formatter.parse(
        this.datePipe.transform(this.minDate, 'yyyy-MM-dd')
      );
    } else {
      this.minDate = new Date(1990, 1, 1);
    }
    if (this.maxDate !== null) {
      this.fnMaxDate = this.formatter.parse(
        this.datePipe.transform(this.maxDate, 'yyyy-MM-dd')
      );
    } else {
      this.maxDate = new Date(2050, 1, 1);
    }
    this.maxDate = this.futureDateDisabled ? new Date() : this.maxDate;
  }

  dateDiffrence(date1: Date, date2: Date) {
    date1.setHours(0);
    date1.setMinutes(0);
    date2.setHours(0);
    date2.setMinutes(0);
    const time = Math.abs(date2.getTime() - date1.getTime());
    const days = Math.ceil(time / (1000 * 60 * 60 * 24));
    return { days, time };
  }

  /**
   * Member Method
   * Method Call When User click on Date in DatePicker.
   * @param date of Type NgbDate.
   */
  onDateSelection(date: NgbDate) {
    this.validButton = false;
    this.isAddPrevClass = true
    if (!this.fnStartDate && !this.fnEndDate) {
      this.fnStartDate = date;
      this.uiDateValue =
        this.getFormatedDate(new Date(this.formatter.format(date))) + '-';
    } else if (
      this.fnStartDate &&
      !this.fnEndDate &&
      date.after(this.fnStartDate)
    ) {
      this.fnEndDate = date;
      this.isAddPrevClass = false;
      this.uiDateValue += this.getFormatedDate(
        new Date(this.formatter.format(date))
      );
    } else {
      this.fnEndDate = null;
      this.fnStartDate = date;
      this.uiDateValue =
        this.getFormatedDate(new Date(this.formatter.format(date))) + '-';
    }

    if (this.enableDateRange) {
      if (this.fnStartDate && this.fnEndDate) {
        const dateDiffMap = this.dateDiffrence(
          new Date(this.formatter.format(this.fnStartDate)),
          new Date(this.formatter.format(this.fnEndDate))
        );

        this.validButton = Math.abs(dateDiffMap.days) > this.maxDayNum;
      }
    }
  }
  /**
   * Member Method
   * method Called when user Clicked on apply Button and Emit Changes.
   * @param datePicker ngbInputDatePicker Ref
   */
  submit(datePicker: NgbInputDatepicker) {
    if (this.validButton) {
      this.fnToaster.error(this.validationMsg);
      return;

    }
    if (isNull(this.fnEndDate)) {
      this.fnEndDate = this.fnStartDate;
    }
    this.startDate = new Date(this.formatter.format(this.fnStartDate));
    this.endDate = new Date(this.formatter.format(this.fnEndDate));
    this.dateCB.emit({
      startDate: this.startDate,
      endDate: this.endDate,
    });
    this.setFormatedDate();
    datePicker.close();
  }
  /**
   * Member Method
   * method to set Default start and end Date when start and End Date is null.
   */
  setDefaultDate() {
    if (this.startDate == null) {
      this.startDate = new Date();
    }
    if (this.endDate == null) {
      this.endDate = new Date();
    }
    this.fnStartDate = this.formatter.parse(
      this.datePipe.transform(this.startDate, 'yyyy-MM-dd')
    );
    this.fnEndDate = this.formatter.parse(
      this.datePipe.transform(this.endDate, 'yyyy-MM-dd')
    );
    this.setFormatedDate();
  }
  /**
   * Member Method
   * Called to Close DatePicker.
   * @param datePicker NgbInputDatepicker Ref
   */
  resetDate(datePicker: NgbInputDatepicker) {
    datePicker.close();
  }
  /**
   * Member Method
   * method Used to Bind close Event of DatePicker to set isOpened value to False.
   */
  onClosed() {
    this.isOpened = false;
  }

  /**
   * Hosts listener to cose dropdown when user click outside Filter Component
   * @param event Event ref to check user click outside Filter Component
   */
  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (!this.elemRef.nativeElement.contains(event.target) && this.isOpened) {
      this.ngbDatePicker.close();
      event.stopPropagation();
    }
  }
  /**
   * Member Method
   * used To add faded class on DatePicker Dates When return true.
   * @param date Type of NgbDate
   * @returns boolean Value
   */
  isHovered(date: NgbDate) {
    return this.isDisbled(date)
      ? false
      : this.fnStartDate &&
      !this.fnEndDate &&
      this.hoveredDate &&
      date.after(this.fnStartDate) &&
      date.before(this.hoveredDate);
  }

  /**
   * Member Method
   * add the faded class between start and End Date in DatePicker Dates.
   * @param date Type of NgbDate
   * @returns boolean Value
   */
  isInside(date: NgbDate) {
    return this.isDisbled(date)
      ? false
      : date.after(this.fnStartDate) && date.before(this.fnEndDate);
  }

  /**
   * Member Method
   * used to show the Selected Range on DateRangePicker.
   * @param date Type of NgbDate
   * @returns boolean Value
   */
  isRange(date: NgbDate) {
    return this.isInside(date) || this.isHovered(date);
  }
  isStartDate(date: NgbDate) {
    return date.equals(this.fnStartDate);
  }
  isEndDate(date: NgbDate) {
    return date.equals(this.fnEndDate);
  }
  /**
   * Member variable
   * assigned a function to varible to disable date if FutureDateDisable is True .
   * And is called for each new date when navigating to a different month..
   * Takes @param date of Type NgbDate
   */
  isDisbled = (date: NgbDate) => {
    return this.futureDateDisabled
      ? new Date(this.formatter.format(date)) >
      new Date(this.formatter.format(this.fnMaxDate))
      : false;
  };
  /**
   * Member function
   * @param date DateObject
   * @returns the DateObject in given Format
   */
  getFormatedDate(date: Date) {
    return this.fnDateFormattter.format(date, this.dateFormat);
  }
  /**
   * Member Function
   * set the selected End and Start Date in given Format to show in UI.
   */
  setFormatedDate() {
    this.uiDateValue =
      this.getFormatedDate(this.startDate) +
      ' - ' +
      this.getFormatedDate(this.endDate);
  }
  /**
   * Member function
   * return class date
   * @param date ngbDate ref
   * @returns class for date
   */
  getDateClass(date: NgbDate) {
    let customClass = 'custom-day ';
    if (this.isDisbled(date)) {
      return customClass;
    }
    if (this.isStartDate(date)) {
      customClass += 'range date-range-start ';
      customClass += this.isAddPrevClass ? 'ngb-day-prev-start' : '';
    } else if (this.isEndDate(date)) {
      customClass += 'range date-range-end';
    } else if (this.isRange(date)) {
      customClass += 'range ';
      customClass += this.isAddPrevClass ? 'ngb-day-prev-range' : '';
    } else if (
      !isNull(this.fnStartDate) &&
      isNull(this.fnEndDate) &&
      date.equals(this.hoveredDate)
    ) {
      customClass += 'ngb-day-prev-end';
    }
    return customClass;
  }
}
